import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Three-way theme: `light` and `dark` are explicit overrides; `system`
// follows the OS preference and updates live when the user toggles
// dark mode at the OS level. Persisted in localStorage so reloads
// don't flicker.

export type ThemePreference = "light" | "dark" | "system";

interface ThemeContextValue {
  // What the user picked (one of the three).
  preference: ThemePreference;
  // What's actually applied to <html> right now (`light` or `dark`).
  resolved: "light" | "dark";
  setPreference: (next: ThemePreference) => void;
}

const STORAGE_KEY = "rua-watch:theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Hint to native form controls (scrollbar, default text inputs)
  // about which color scheme they should render in.
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<"light" | "dark">(() =>
    readStoredPreference() === "dark"
      ? "dark"
      : readStoredPreference() === "light"
      ? "light"
      : systemPrefersDark()
      ? "dark"
      : "light",
  );

  // Apply the resolved theme on every change, plus once on mount so
  // the very first paint matches the stored preference.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // When preference is "system", keep `resolved` in sync with the OS.
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  const setPreference = (next: ThemePreference) => {
    setPreferenceState(next);
    localStorage.setItem(STORAGE_KEY, next);
    if (next === "system") setResolved(systemPrefersDark() ? "dark" : "light");
    else setResolved(next);
  };

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

// Three-button toggle. Compact enough to live in a sidebar footer.
export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const opt = (value: ThemePreference, label: string, icon: ReactNode) => {
    const active = preference === value;
    return (
      <button
        key={value}
        onClick={() => setPreference(value)}
        title={label}
        aria-label={label}
        aria-pressed={active}
        className={[
          "flex h-7 w-7 items-center justify-center rounded transition-colors",
          active
            ? "bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200",
        ].join(" ")}
      >
        {icon}
      </button>
    );
  };

  return (
    <div className="inline-flex gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
      {opt("light", "Light theme", <SunIcon />)}
      {opt("system", "System theme", <MonitorIcon />)}
      {opt("dark", "Dark theme", <MoonIcon />)}
    </div>
  );
}

// Inline SVG icons — keeps the chunk free of an icon-pack dependency.
// Numbers in viewBox / path data look duplicated on purpose; an
// earlier dedup tool stripped repeated tokens here and broke the
// glyphs. Don't run the same pass over this file again.

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
